import { useState, useEffect } from 'react';
import { Auth, API, graphqlOperation } from 'aws-amplify';
import { CreateSentenceMutation, ListSentencesQuery } from 'API';
import {
  createSentence,
  deleteSentence,
  updateSentence,
} from 'graphql/mutations';

type Props = {
  needToFetch: boolean;
};

type CreateProps = Omit<
  CreateSentenceMutation['createSentence'],
  'id' | 'userId' | '__typename' | 'createdAt' | 'updatedAt'
>;
type SentenceList = ListSentencesQuery['listSentences']['items'];
const useSentence = (
  props: Props
): {
  isLoading: boolean;
  isError: boolean;
  sentenceList: SentenceList;
  fetch: () => Promise<SentenceList>;
  register: (props: CreateProps) => void;
  deleteOne: (id: string) => void;
  getOne: (id: string) => Promise<{ jp: string; en: string; checked: number }>;
  update: (id: string, checked: number) => void;
} => {
  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const [sentenceList, setSentence] = useState<SentenceList>([]);

  const update = async (id: string, checked: number) => {
    try {
      setLoading(true);
      await API.graphql(
        graphqlOperation(updateSentence, {
          input: { id, checked },
        })
      );
      await fetch();
    } catch (e) {
      console.log(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const getOne = async (
    id: string
  ): Promise<{ jp: string; en: string; checked: number }> => {
    try {
      setLoading(true);
      const sentence = (await API.graphql(
        graphqlOperation(
          `
        query GetSentence($id: ID!) {
          getSentence(id: $id) {
            id
            userId
            jp
            en
            checked
            createdAt
            updatedAt
          }
        }
      `,
          {
            id,
          }
        )
      )) as {
        data: { getSentence: { jp: string; en: string; checked: number } };
      };
      return sentence.data.getSentence;
    } catch (e) {
      console.log(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const deleteOne = async (id: string) => {
    try {
      setLoading(true);
      await API.graphql(
        graphqlOperation(deleteSentence, {
          input: { id },
        })
      );
    } catch (e) {
      console.log(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetch = async (): Promise<SentenceList> => {
    try {
      setLoading(true);
      const user = (await Auth.currentAuthenticatedUser()).signInUserSession
        .accessToken.payload['username'];
      const result = (await API.graphql(
        graphqlOperation(
          `
        query SentenceByUserId(
          $userId: String!
          $createdAt: ModelStringKeyConditionInput
          $sortDirection: ModelSortDirection
          $filter: ModelSentenceFilterInput
          $limit: Int
          $nextToken: String
        ) {
          sentenceByUserId(
            userId: $userId
            createdAt: $createdAt
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              userId
              jp
              en
              checked
              createdAt
              updatedAt
            }
            nextToken
          }
        }
      `,
          {
            userId: user,
            sortDirection: 'ASC',
            limit: 1000
          }
        )
      )) as {
        data: {
          sentenceByUserId: {
            items: ListSentencesQuery['listSentences']['items'];
          };
        };
      };
      setSentence(result.data.sentenceByUserId.items);
      return result.data.sentenceByUserId.items;
    } catch (e) {
      console.log(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const register = async (props: CreateProps) => {
    try {
      setLoading(true);
      const user = (await Auth.currentAuthenticatedUser()).signInUserSession
        .accessToken.payload['username'];
      await API.graphql(
        graphqlOperation(createSentence, {
          input: { ...props, userId: user },
        })
      );
    } catch (e) {
      console.log(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props === undefined) return;
    const { needToFetch } = props;
    let unmounted = false;
    const f = async () => {
      if (needToFetch && !unmounted) {
        fetch();
      }
    };
    f();
    const cleanup = () => {
      unmounted = true;
    };
    return cleanup;
  }, []);

  return {
    isLoading,
    isError,
    sentenceList,
    getOne,
    fetch,
    register,
    deleteOne,
    update,
  };
};
export default useSentence;
